import styled from '@emotion/styled';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from '../../hooks/useRouter';
import { useCurrentUser } from '../../services/currentUser';
import { useFeatureEnabled } from '../../services/features';
import { useIsOrgAIEnabled } from '../../services/orgSettings';
import { useCopilotMaybe } from './CopilotContext';

const GradientPill = styled('button')`
  height: 32px;
  padding: 0 14px;
  border: none;
  border-radius: 999px;
  background: ${(p) => p.theme.dado.copilot.gradientHorizontal};
  color: #fff;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  box-shadow: ${(p) => p.theme.dado.copilot.pillShadow};
  white-space: nowrap;
  transition: filter 120ms ease, transform 120ms ease;

  &:hover {
    filter: brightness(1.05);
  }
  &:active {
    transform: translateY(0.5px);
  }
  &:focus-visible {
    outline: none;
    box-shadow:
      ${(p) => p.theme.dado.copilot.pillShadow},
      0 0 0 3px rgba(14, 120, 228, 0.25);
  }
`;

// Small "BETA" badge that rides inside the gradient pill (dark translucent
// chip so it reads against the gradient — matches the nav design).
const Beta = styled('span')`
  align-self: flex-start;
  margin-top: 8px;
  font-size: 6.5px;
  font-weight: 700;
  letter-spacing: 0.06em;
  padding: 1.5px 4px;
  border-radius: 2px;
  background: rgb(4, 80, 156);
  color: rgb(255, 255, 255);
  line-height: 1;
`;

export const CopilotButton: React.FC = () => {
  const { t } = useTranslation();
  const ctx = useCopilotMaybe();
  // react-router-dom 5.0.0 on verifiable predates hooks; useRouter()
  // (via __RouterContext) gives a reactive location instead of useLocation.
  const { location } = useRouter();
  const pathname = location.pathname;
  const { org } = useCurrentUser();
  // Mirror the same access gate as <DadoCopilotGated>. The provider lives
  // at App.tsx:446 (above this button in the tree), so `useCopilotMaybe()`
  // always finds a context — the original "provider absent → hide button"
  // path no longer fires. Without these flag checks the button leaks onto
  // /experiences for orgs that have the copilot disabled.
  const hasCopilotFlag = useFeatureEnabled('AI_COPILOT');
  const isAIEnabled = useIsOrgAIEnabled(org.id);
  // Production safety gate — restrict to Dado employees, prod only.
  // Dev/staging keep the broader flag-based access so internal QA
  // isn't blocked. Both this and the isDadoEmployee gate are
  // temporary and will be lifted as dogfood matures.
  if (!hasCopilotFlag || !isAIEnabled) return null;

  // If the provider isn't mounted (defensive — currently always mounted
  // via App.tsx, but keeps the button safe if the wiring changes again).
  if (!ctx) return null;

  // Show only on /experiences and /experiences/* — the copilot is scoped to
  // the experience-builder workflow. Hidden on every other page.
  const onExperiencesPage =
    pathname === '/experiences' || pathname.startsWith('/experiences/');
  if (!onExperiencesPage) return null;

  return (
    <GradientPill onClick={ctx.toggle} type="button">
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="currentColor"
        aria-hidden="true"
      >
        <path d="M6 0L7.2 4.8L12 6L7.2 7.2L6 12L4.8 7.2L0 6L4.8 4.8L6 0Z" />
      </svg>
      {t('Assistant')}
      <Beta>BETA</Beta>
    </GradientPill>
  );
};
